import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ContractLedger } from '@wsphere/warranties/domain';
import { RatingMatrixService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.utils';

const UNASSIGNED = '';
const CONTEXT_SELLER = `${ContractLedger.PartyType.SELLER}|`;

interface RecipientOption {
  key: string;
  label: string;
}

/**
 * Picks who a Fee Type's (default) or rating-matrix cell's (override) amount is intended for:
 * a party in one of its roles, the contextual seller, or nobody. Parties themselves are managed
 * on the policy's Parties tab.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-fee-recipient-picker',
  templateUrl: './fee-recipient-picker.component.html',
  standalone: false,
})
export class FeeRecipientPickerComponent implements OnInit {
  @Input() value: ContractLedger.RecipientAssignment | null = null;
  /** Label for the empty choice — e.g. "Use Fee Type default" on a cell. */
  @Input() unassignedLabel = 'No recipient';

  @Output() valueChanges = new EventEmitter<ContractLedger.RecipientAssignment | null>();

  options: RecipientOption[] = [];

  constructor(
    private ratingMatrixSvc: RatingMatrixService,
    private toastSvc: ToastService
  ) {}

  get selectedKey(): string {
    return this.value ? `${this.value.role}|${this.value.partyRecordId ?? ''}` : UNASSIGNED;
  }

  async ngOnInit() {
    await this.loadParties();
  }

  // vs-select emits `readonly T[] | T | null` (it supports multi-select); this one is single-select.
  select(value: unknown) {
    const key = typeof value === 'string' ? value : null;
    if (!key) {
      this.value = null;
    } else {
      const [role, partyRecordId] = key.split('|');
      this.value = { role: role as ContractLedger.PartyType, partyRecordId: partyRecordId || null };
    }
    this.valueChanges.emit(this.value);
  }

  private async loadParties() {
    const fixed = [
      { key: UNASSIGNED, label: this.unassignedLabel },
      { key: CONTEXT_SELLER, label: 'Seller (the selected distributor in quick sale)' },
    ];
    try {
      const parties = await this.ratingMatrixSvc.listParties();
      this.options = [
        ...fixed,
        ...parties.flatMap(party =>
          party.roles.map(role => ({
            key: `${role}|${party.id}`,
            label: `${party.name} — ${ContractLedger.partyTypeLabel(role)}`,
          }))
        ),
      ];
    } catch (err) {
      this.options = fixed;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to load parties',
        text: escapeHtml((err as { error?: { message?: string } })?.error?.message ?? 'Something went wrong.'),
      });
    }
  }
}
